import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { HiringAccessLevelIdsEnum } from '@applicant-tracker/enums/hiring-access-levels.enum';
import { Employee } from '@models/employee/employee.model';
import { DiscussionComment } from '@models/recruiting/discussion-comment.model';
import cloneDeep from 'lodash-es/cloneDeep';

interface Mention {
    label: string;
    employee: Employee;
    avatarId?: number;
}

@Component({
    selector: 'app-discussion-comment',
    templateUrl: './comment.template.html',
    styleUrls: ['./comment.style.scss'],
})
export class CommentComponent implements OnInit {
    @Input() comment: DiscussionComment = new DiscussionComment();
    @Input() new = false;
    @Input() miniState = false;
    @Input() hiringTeam: Employee[] = [];
    @Output() save: EventEmitter<DiscussionComment> = new EventEmitter<DiscussionComment>();
    @Output() delete: EventEmitter<DiscussionComment> = new EventEmitter<DiscussionComment>();
    isUpdated = false;
    editable = false;
    canUpdate = false;
    canDelete = false;

    hiringAccessLevelIdsEnum = HiringAccessLevelIdsEnum;
    mentions: Mention[] = [];
    private attachedMentions: Mention[] = [];
    private commentClone: DiscussionComment | null = null;

    constructor(private auth: AuthService) {}

    get tags(): Translatable {
        switch (this.comment.accessLevelId) {
            case 0:
                return 'applicant-tracker.components.comment.@onlyRecruiters';

            case this.hiringAccessLevelIdsEnum.basic:
                return 'applicant-tracker.components.comment.@everyoneOnTheHiringTeam';

            case this.hiringAccessLevelIdsEnum.hiringManager:
                return 'applicant-tracker.components.comment.@onlyRecruitersHiringManagers';

            case this.hiringAccessLevelIdsEnum.private:
                return 'applicant-tracker.components.comment.@privateOnlyMe';
        }

        return this.comment.taggedEmployees.map((e) => `@${e.firstName}`).join(', ');
    }

    get isUpdatable(): boolean {
        return !!this.comment.id;
    }

    get isSavable(): boolean {
        const canSave = this.comment.accessLevelId !== undefined;

        return !!this.comment.message && canSave;
    }

    get rowsCount(): number {
        if (!this.comment.message) {
            return 1;
        }

        return this.comment.message.split(/\r\n|\r|\n/).length;
    }

    async ngOnInit(): Promise<void> {
        this.commentClone = cloneDeep(this.comment);

        this.isUpdated =
            this.isUpdatable &&
            new Date(this.comment.createdAt).getTime() !== new Date(this.comment.updatedAt).getTime();

        this.canUpdate = this.isUpdatable && this.comment.author.is(this.auth.employee);
        this.canDelete = this.isUpdatable && (this.canUpdate || this.auth.isAdmin());

        this.mentions = this.hiringTeam
            .filter((e) => e.id !== this.auth.employee.id)
            // Note that employee data should be validated so that `fullName`
            // doesn't have to be `string | null`. This is temporary.
            .map((e) => ({ label: e.fullName || '', employee: e, avatarId: e.avatarId }));
    }

    cancelUpdate(): void {
        if (this.commentClone !== null) {
            this.comment = cloneDeep(this.commentClone);
        }
        this.attachedMentions = [];
        this.editable = false;
    }

    onSave(): void {
        this.syncMentions();

        if (this.isDirty()) {
            this.save.emit(this.comment);
        }

        if (this.isUpdatable) {
            this.editable = false;
            return;
        }

        this.resetComment();
    }

    onDelete(): void {
        this.delete.emit(this.comment);
    }

    attachMention(mention: Mention): void {
        this.attachedMentions.push(mention);
    }

    private syncMentions(): void {
        const employeesToAttachIds = this.attachedMentions
            .filter((m) => this.comment.message.includes(`@${m.label}`))
            .map((m) => m.employee.id);

        if (employeesToAttachIds.length) {
            this.comment.attach('employees', [...new Set(employeesToAttachIds)]);
        }

        if (!this.comment.taggedEmployees) {
            return;
        }

        const employeesToDetachIds = this.comment.taggedEmployees
            .filter((e) => !this.comment.message.includes(`@${e.fullName}`))
            .map((e) => e.id);

        if (!employeesToDetachIds.length) {
            return;
        }

        this.comment.detach('employees', [...new Set(employeesToDetachIds)]);
    }

    private resetComment(): void {
        this.comment = new DiscussionComment();
        this.attachedMentions = [];
    }

    private isDirty(): boolean {
        return this.comment.message.trim() !== this.commentClone?.message?.trim();
    }
}
